/**
 * PacienteHistorialPage — M6: Clinical history for a single patient.
 *
 * Route: /paciente/:pacienteId/historial
 * Access: medico, admin, supervisor
 *
 * Shows:
 *   - Patient demographics (decrypted via portal_obtenerPaciente)
 *   - Full consultation history accordion (portal_obtenerHistorialConsultas)
 *   - Per-consultation detail on expand (portal_obtenerDetalleConsulta)
 *   - Audio player for recordings (portal_obtenerUrlGrabacion)
 *   - CSV export (portal_exportarDatos)
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, ChevronRight,
  FileText, Clock, Mic, AlertTriangle, User, Activity, ExternalLink,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, orderBy, limit as fbLimit, getDocs } from 'firebase/firestore';
import { functions, db } from '../config/firebase';
import MainLayout from '../components/layout/MainLayout';
import { Button, Card, Skeleton } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { trackError } from '../services/analytics';
import styles from './PacienteHistorialPage.module.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Paciente {
  id: string;
  nombreCompleto: string;
  cedula?: string;
  fechaNacimiento?: string;
  sexo?: string;
  email?: string;
  telefono?: string;
  grupoSanguineo?: string;
  alergias?: string[];
  condicionesCronicas?: string[];
  medicamentosActuales?: string[];
}

interface ConsultaResumen {
  id: string;
  pacienteNombre: string;
  motivoConsulta: string;
  medicoNombre?: string;
  salaNombre?: string;
  estado: string;
  disposicion?: string;
  duracionSegundos?: number;
  notas?: string;
  creadoEn?: { toDate?: () => Date };
  finalizadaEn?: { toDate?: () => Date };
  tieneGrabacion: boolean;
  tieneTranscripcion: boolean;
}

interface DetalleConsulta {
  consulta: ConsultaResumen & {
    grabacion?: { disponible: boolean; duracionSeg?: number; tamanoBytes?: number };
    calidadLlamada?: number;
  };
  notas: Array<Record<string, unknown>>;
  transcripcion: { segmentos: number; totalPalabras: number; porHablante: Array<{ hablante: string; fragmentos: number; extracto: string }> };
  eventos: Array<{ id: string; tipo: string; creadoEn?: unknown }>;
}

// ── Callables ─────────────────────────────────────────────────────────────────

const obtenerPacienteFn    = httpsCallable(functions, 'portal_obtenerPaciente');
const obtenerHistorialFn   = httpsCallable(functions, 'portal_obtenerHistorialConsultas');
const obtenerDetalleFn     = httpsCallable(functions, 'portal_obtenerDetalleConsulta');
const obtenerUrlGrabacionFn = httpsCallable(functions, 'portal_obtenerUrlGrabacion');

// ── Helpers ───────────────────────────────────────────────────────────────────

function toDate(ts: { toDate?: () => Date } | undefined): Date | null {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return null;
}

function formatDate(ts: { toDate?: () => Date } | undefined): string {
  const d = toDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDuration(secs?: number): string {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
}

function disposicionLabel(d?: string): string {
  const map: Record<string, string> = {
    completada: 'Completada',
    paciente_no_presento: 'No se presentó',
    fallo_tecnico: 'Fallo técnico',
    cancelada_medico: 'Cancelada',
    incompleta: 'Incompleta',
    referida: 'Referida',
  };
  return d ? (map[d] ?? d) : '—';
}

function disposicionColor(d?: string): string {
  if (d === 'completada') return styles.tagGreen;
  if (d === 'referida') return styles.tagBlue;
  if (d === 'paciente_no_presento' || d === 'cancelada_medico') return styles.tagOrange;
  if (d === 'fallo_tecnico' || d === 'incompleta') return styles.tagRed;
  return styles.tagGray;
}

// ── SOAP normalization ──────────────────────────────────────────────────────────
// Las notas se guardan en dos formas: `soap_final` (firmada) con s/o/a/p/e al
// nivel raíz, y `ia_soap` con `soap: { subjective, objective, assessment, plan,
// education }`. Normalizamos ambas a { s, o, a, p, e } para mostrarlas.

type SoapShape = { s?: string; o?: string; a?: string; p?: string; e?: string };

function normalizeSoap(nota: Record<string, unknown> | undefined | null): SoapShape | null {
  if (!nota) return null;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? (v as string) : undefined);
  // soap_final: campos al nivel raíz
  const top: SoapShape = { s: str(nota.s), o: str(nota.o), a: str(nota.a), p: str(nota.p), e: str(nota.e) };
  if (top.s || top.o || top.a || top.p || top.e) return top;
  // ia_soap: anidado en `soap` (subjective/objective/... o s/o/a/p)
  const sp = nota.soap as Record<string, unknown> | undefined;
  if (sp) {
    return {
      s: str(sp.s) ?? str(sp.subjective),
      o: str(sp.o) ?? str(sp.objective),
      a: str(sp.a) ?? str(sp.assessment),
      p: str(sp.p) ?? str(sp.plan),
      e: str(sp.e) ?? str(sp.evolution) ?? str(sp.education),
    };
  }
  return null;
}

/** Elige el SOAP a mostrar: la nota firmada (soap_final), o el documento único
 *  `nota_actual` si la consulta aún no se firmó. (NU: ya no hay notas ia_soap.) */
function pickSoap(notas: Array<Record<string, unknown>>): SoapShape | null {
  if (!notas?.length) return null;
  const hasContent = (s: SoapShape | null) => !!(s && (s.s || s.o || s.a || s.p || s.e));
  const fin = normalizeSoap(notas.find((n) => n?.tipo === 'soap_final'));
  if (hasContent(fin)) return fin;
  const actual = normalizeSoap(notas.find((n) => n?.tipo === 'nota_actual'));
  if (hasContent(actual)) return actual;
  return null;
}

const SOAP_SECTIONS: Array<{ k: keyof SoapShape; label: string; cls: string }> = [
  { k: 's', label: 'Subjetivo', cls: 'soapS' },
  { k: 'o', label: 'Objetivo', cls: 'soapO' },
  { k: 'a', label: 'Análisis', cls: 'soapA' },
  { k: 'p', label: 'Plan', cls: 'soapP' },
  { k: 'e', label: 'Evolución', cls: 'soapE' },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function PacienteHistorialPage() {
  const { pacienteId } = useParams<{ pacienteId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [paciente, setPaciente]         = useState<Paciente | null>(null);
  const [consultas, setConsultas]       = useState<ConsultaResumen[]>([]);
  const [loading, setLoading]           = useState(true);
  const [expandedId, setExpandedId]     = useState<string | null>(null);
  const [detalles, setDetalles]         = useState<Record<string, DetalleConsulta>>({});
  const [loadingDetalle, setLoadingDetalle] = useState<string | null>(null);
  const [audioUrls, setAudioUrls]       = useState<Record<string, string>>({});

  // ── Load patient + history ─────────────────────────────────────────────────

  useEffect(() => {
    if (!pacienteId) return;

    async function load() {
      // Try Cloud Functions first; fall back to direct Firestore reads
      // when the functions are not deployed (404) or fail (500).
      const [pacResult, histResult] = await Promise.allSettled([
        obtenerPacienteFn({ pacienteId }),
        obtenerHistorialFn({ pacienteId, limit: 50 }),
      ]);

      if (pacResult.status === 'fulfilled') {
        setPaciente((pacResult.value.data as { paciente: Paciente }).paciente);
      } else {
        trackError('PacienteHistorialPage.paciente', pacResult.reason);
      }

      if (histResult.status === 'fulfilled') {
        setConsultas((histResult.value.data as { consultas: ConsultaResumen[] }).consultas);
      } else {
        // Fallback: read directly from Firestore
        trackError('PacienteHistorialPage.historial.fn', histResult.reason);
        try {
          const q = query(
            collection(db, 'consultas'),
            where('pacienteId', '==', pacienteId),
            where('estado', '==', 'finalizada'),
            orderBy('finalizadaEn', 'desc'),
            fbLimit(50),
          );
          const snap = await getDocs(q);
          setConsultas(snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              pacienteNombre: data.pacienteNombre ?? '',
              motivoConsulta: data.motivoConsulta ?? '',
              medicoNombre: data.medicoNombre,
              salaNombre: data.salaNombre,
              estado: data.estado,
              disposicion: data.disposicion,
              duracionSegundos: data.duracionSegundos,
              notas: data.notas,
              creadoEn: data.creadoEn,
              finalizadaEn: data.finalizadaEn,
              tieneGrabacion: !!data.grabacionCompletada,
              tieneTranscripcion: !!data.transcripcionCompletada,
            } as ConsultaResumen;
          }));
        } catch (fbErr) {
          trackError('PacienteHistorialPage.historial.firestore', fbErr);
          // Last resort: query without ordering (no composite index needed)
          try {
            const qSimple = query(
              collection(db, 'consultas'),
              where('pacienteId', '==', pacienteId),
              where('estado', '==', 'finalizada'),
              fbLimit(50),
            );
            const snap = await getDocs(qSimple);
            setConsultas(snap.docs.map((d) => {
              const data = d.data();
              return {
                id: d.id,
                pacienteNombre: data.pacienteNombre ?? '',
                motivoConsulta: data.motivoConsulta ?? '',
                medicoNombre: data.medicoNombre,
                salaNombre: data.salaNombre,
                estado: data.estado,
                disposicion: data.disposicion,
                duracionSegundos: data.duracionSegundos,
                notas: data.notas,
                creadoEn: data.creadoEn,
                finalizadaEn: data.finalizadaEn,
                tieneGrabacion: !!data.grabacionCompletada,
                tieneTranscripcion: !!data.transcripcionCompletada,
              } as ConsultaResumen;
            }));
          } catch (lastErr) {
            trackError('PacienteHistorialPage.historial.fallback', lastErr);
            addToast('error', 'Error cargando historial de consultas');
          }
        }
      }

      setLoading(false);
    }

    void load();
  }, [pacienteId, addToast]);

  // ── Expand / load detail ───────────────────────────────────────────────────

  const handleExpand = useCallback(async (consultaId: string) => {
    if (expandedId === consultaId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(consultaId);
    if (detalles[consultaId]) return; // already loaded

    setLoadingDetalle(consultaId);
    try {
      const res = await obtenerDetalleFn({ consultaId });
      setDetalles((prev) => ({
        ...prev,
        [consultaId]: (res.data as { consulta: unknown; notas: unknown[]; transcripcion: unknown; eventos: unknown[] }) as unknown as DetalleConsulta,
      }));
    } catch (err) {
      trackError('PacienteHistorialPage.detalle.fn', err);
      // Fallback: read consultation doc + subcollections directly from Firestore
      try {
        const consultaSnap = await getDoc(doc(db, 'consultas', consultaId));
        if (!consultaSnap.exists()) throw new Error('Consulta no encontrada');
        const cData = consultaSnap.data()!;

        // Read subcollections in parallel — each may fail if index is missing
        const [notasSnap, transcripcionSnap, eventosSnap] = await Promise.allSettled([
          getDocs(query(collection(db, `consultas/${consultaId}/notas_medicas`), fbLimit(10))),
          getDocs(query(collection(db, `consultas/${consultaId}/transcripcion`), fbLimit(500))),
          getDocs(query(collection(db, `consultas/${consultaId}/eventos`), fbLimit(100))),
        ]);

        const notasDocs = notasSnap.status === 'fulfilled' ? notasSnap.value.docs : [];
        const transcDocs = transcripcionSnap.status === 'fulfilled' ? transcripcionSnap.value.docs : [];
        const eventosDocs = eventosSnap.status === 'fulfilled' ? eventosSnap.value.docs : [];

        // Summarise transcript by speaker
        const porHablante: Record<string, string[]> = {};
        for (const d of transcDocs) {
          const { hablante, texto } = d.data() as { hablante: string; texto: string };
          if (!porHablante[hablante]) porHablante[hablante] = [];
          porHablante[hablante].push(texto);
        }

        const detalle: DetalleConsulta = {
          consulta: {
            id: consultaId,
            pacienteNombre: cData.pacienteNombre ?? '',
            motivoConsulta: cData.motivoConsulta ?? '',
            medicoNombre: cData.medicoNombre,
            salaNombre: cData.salaNombre,
            estado: cData.estado,
            disposicion: cData.disposicion,
            duracionSegundos: cData.duracionSegundos,
            notas: cData.notas,
            creadoEn: cData.creadoEn,
            finalizadaEn: cData.finalizadaEn,
            tieneGrabacion: !!cData.grabacionCompletada,
            tieneTranscripcion: !!cData.transcripcionCompletada,
            calidadLlamada: cData.calidadLlamada,
            grabacion: cData.grabacionCompletada
              ? { disponible: true, duracionSeg: cData.grabacionDuracionSeg, tamanoBytes: cData.grabacionTamanoBytes }
              : undefined,
          },
          notas: notasDocs.map((d) => ({ id: d.id, ...d.data() })) as DetalleConsulta['notas'],
          transcripcion: {
            segmentos: transcDocs.length,
            totalPalabras: transcDocs.reduce((acc, d) => acc + ((d.data().texto as string)?.split(' ').length ?? 0), 0),
            porHablante: Object.entries(porHablante).map(([hablante, textos]) => ({
              hablante,
              fragmentos: textos.length,
              extracto: textos.slice(0, 3).join(' ').slice(0, 200),
            })),
          },
          eventos: eventosDocs.map((d) => ({ id: d.id, ...d.data() })) as DetalleConsulta['eventos'],
        };

        setDetalles((prev) => ({ ...prev, [consultaId]: detalle }));
      } catch (fbErr) {
        trackError('PacienteHistorialPage.detalle.firestore', fbErr);
        addToast('error', 'Error cargando detalle de la consulta');
      }
    } finally {
      setLoadingDetalle(null);
    }
  }, [expandedId, detalles, addToast]);

  // ── Load signed audio URL ──────────────────────────────────────────────────

  const handlePlayRecording = useCallback(async (consultaId: string) => {
    if (audioUrls[consultaId]) return; // already loaded
    try {
      const res = await obtenerUrlGrabacionFn({ consultaId });
      const { url } = res.data as { url: string };
      setAudioUrls((prev) => ({ ...prev, [consultaId]: url }));
    } catch (err) {
      trackError('PacienteHistorialPage.recording', err);
      addToast('error', 'No se pudo cargar la grabación');
    }
  }, [audioUrls, addToast]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MainLayout>
      <div className={styles.page}>

        {/* Header */}
        <div className={styles.header}>
          <button
            className={styles.backBtn}
            onClick={() => navigate(-1)}
            aria-label="Volver"
          >
            <ArrowLeft size={18} aria-hidden="true" />
          </button>
          <div className={styles.headerTitle}>
            <h1 className={styles.title}>
              {loading
                ? <Skeleton width={200} height={24} />
                : (paciente?.nombreCompleto || consultas[0]?.pacienteNombre || 'Paciente')}
            </h1>
            <span className={styles.subtitle}>Historial clínico completo</span>
          </div>
        </div>

        {/* Demographics card */}
        {loading ? (
          <Card className={styles.demoCard}>
            <Skeleton height={16} /><Skeleton height={16} /><Skeleton height={16} />
          </Card>
        ) : paciente && (
          <Card className={styles.demoCard}>
            <div className={styles.demoGrid}>
              <div className={styles.demoItem}>
                <span className={styles.demoLabel}><User size={12} aria-hidden="true" /> Cédula</span>
                <span>{paciente.cedula ?? '—'}</span>
              </div>
              <div className={styles.demoItem}>
                <span className={styles.demoLabel}><Activity size={12} aria-hidden="true" /> Fecha nacimiento</span>
                <span>{paciente.fechaNacimiento ?? '—'}</span>
              </div>
              <div className={styles.demoItem}>
                <span className={styles.demoLabel}>Sexo</span>
                <span>{paciente.sexo ?? '—'}</span>
              </div>
              <div className={styles.demoItem}>
                <span className={styles.demoLabel}>Grupo sanguíneo</span>
                <span>{paciente.grupoSanguineo ?? '—'}</span>
              </div>
              {(paciente.alergias?.length ?? 0) > 0 && (
                <div className={`${styles.demoItem} ${styles.demoFull}`}>
                  <span className={styles.demoLabel}><AlertTriangle size={12} aria-hidden="true" /> Alergias</span>
                  <span className={styles.tagList}>
                    {paciente.alergias!.map((a) => (
                      <span key={a} className={`${styles.tag} ${styles.tagRed}`}>{a}</span>
                    ))}
                  </span>
                </div>
              )}
              {(paciente.condicionesCronicas?.length ?? 0) > 0 && (
                <div className={`${styles.demoItem} ${styles.demoFull}`}>
                  <span className={styles.demoLabel}>Condiciones crónicas</span>
                  <span className={styles.tagList}>
                    {paciente.condicionesCronicas!.map((c) => (
                      <span key={c} className={`${styles.tag} ${styles.tagBlue}`}>{c}</span>
                    ))}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Consultation history */}
        <div className={styles.historialSection}>
          <h2 className={styles.sectionTitle}>
            <FileText size={16} aria-hidden="true" /> Consultas ({consultas.length})
          </h2>

          {loading ? (
            Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={64} className={styles.skeletonRow} />)
          ) : consultas.length === 0 ? (
            <div className={styles.empty}>Sin consultas registradas</div>
          ) : (
            <div className={styles.accordionList} role="list">
              {consultas.map((c) => {
                const isOpen    = expandedId === c.id;
                const detalle   = detalles[c.id];
                const isLoading = loadingDetalle === c.id;

                return (
                  <div key={c.id} className={styles.accordionItem} role="listitem">
                    {/* Row header */}
                    <button
                      className={`${styles.accordionHeader} ${isOpen ? styles.accordionHeaderOpen : ''}`}
                      onClick={() => handleExpand(c.id)}
                      aria-expanded={isOpen}
                    >
                      <span className={styles.accordionChevron} aria-hidden="true">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                      <div className={styles.accordionMeta}>
                        <span className={styles.consultaFecha}>{formatDate(c.creadoEn)}</span>
                        <span className={styles.consultaMotivo}>{c.motivoConsulta}</span>
                      </div>
                      <div className={styles.accordionRight}>
                        <span className={`${styles.tag} ${disposicionColor(c.disposicion)}`}>
                          {disposicionLabel(c.disposicion)}
                        </span>
                        {c.tieneGrabacion && (
                          <span className={`${styles.tag} ${styles.tagPurple}`} title="Tiene grabación">
                            <Mic size={10} aria-hidden="true" /> Audio
                          </span>
                        )}
                        <span className={styles.consultaDuracion}>
                          <Clock size={11} aria-hidden="true" /> {formatDuration(c.duracionSegundos)}
                        </span>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div className={styles.accordionBody} role="region" aria-label={`Detalle consulta ${formatDate(c.creadoEn)}`}>
                        {isLoading ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Skeleton height={14} /><Skeleton height={14} /><Skeleton height={14} /><Skeleton height={14} />
                          </div>
                        ) : detalle ? (
                          <div className={styles.detalle}>
                            {/* Acceso directo a la consulta médica completa */}
                            <button
                              type="button"
                              className={styles.verConsultaBtn}
                              onClick={() => navigate(`/historial/${c.id}`)}
                              title="Abrir la consulta médica completa"
                            >
                              <FileText size={13} aria-hidden="true" /> Ver consulta médica completa
                              <ExternalLink size={12} aria-hidden="true" />
                            </button>

                            {/* SOAP notes (incluye la E de SOAPE; normaliza ambas formas de guardado) */}
                            {(() => {
                              const soap = pickSoap(detalle.notas);
                              if (!soap) return null;
                              return (
                                <section className={styles.detalleSection}>
                                  <h3 className={styles.detalleSectionTitle}>Nota SOAP</h3>
                                  <div className={styles.soapGrid}>
                                    {SOAP_SECTIONS.map(({ k, label, cls }) => soap[k] && (
                                      <div key={k} className={`${styles.soapItem} ${styles[cls] ?? ''}`}>
                                        <span className={styles.soapLabel}>{label}</span>
                                        <p className={styles.soapText}>{soap[k]}</p>
                                      </div>
                                    ))}
                                  </div>
                                </section>
                              );
                            })()}

                            {/* Transcription summary */}
                            {detalle.transcripcion.segmentos > 0 && (
                              <section className={styles.detalleSection}>
                                <h3 className={styles.detalleSectionTitle}>
                                  Transcripción ({detalle.transcripcion.segmentos} segmentos · {detalle.transcripcion.totalPalabras} palabras)
                                </h3>
                                {detalle.transcripcion.porHablante.map((h) => (
                                  <div key={h.hablante} className={styles.transcriptChunk}>
                                    <span className={styles.hablante}>{h.hablante}</span>
                                    <span className={styles.extracto}>"{h.extracto}…"</span>
                                  </div>
                                ))}
                              </section>
                            )}

                            {/* Audio recording */}
                            {detalle.consulta.grabacion?.disponible && (
                              <section className={styles.detalleSection}>
                                <h3 className={styles.detalleSectionTitle}>
                                  <Mic size={13} aria-hidden="true" /> Grabación de audio
                                </h3>
                                {audioUrls[c.id] ? (
                                  <audio
                                    controls
                                    src={audioUrls[c.id]}
                                    className={styles.audioPlayer}
                                    aria-label="Grabación de la consulta"
                                  />
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handlePlayRecording(c.id)}
                                  >
                                    <Mic size={12} aria-hidden="true" /> Cargar grabación
                                  </Button>
                                )}
                              </section>
                            )}

                            {/* General notes */}
                            {c.notas && (
                              <section className={styles.detalleSection}>
                                <h3 className={styles.detalleSectionTitle}>Notas</h3>
                                <p className={styles.notasText}>{c.notas}</p>
                              </section>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </MainLayout>
  );
}
